import type { AudienceView, OwnerView } from '@ralph/contracts'
import clsx from 'clsx'
import { Fragment } from 'react'
import { GroupLabel } from '@/components/groups/GroupLabel.tsx'
import { useT } from '@/i18n/index.ts'

/**
 * Who can use an agent, drawn (PRD-26 GRP-102): each group as its avatar and its name, linked to
 * the group's page — where "who is in it" is answered — and never an id. Everyone and the private
 * case are words from `marketplace.gallery.about`, so the listing page and the detail sheet —
 * both of which render this component — cannot disagree about one audience.
 *
 * The conjunction between the last two chips is a catalogue entry rather than
 * `useI18n().formatList`, and that is a limit rather than an oversight: `Intl.ListFormat` joins
 * strings, and each item here is a linked avatar plus a name. Both shipped locales punctuate this
 * list identically ("A, B and C" · "A, B e C"), so the drawn form is correct in both; a third
 * language that does not would need `formatToParts` exposed from `i18n/format.ts` to interleave
 * the elements between the locale's own separators.
 */
export const AudienceLabel = ({
  audience,
  owner,
  className
}: {
  audience: AudienceView
  owner: OwnerView
  className?: string
}) => {
  // Inside the body, never captured: the conjunction and the three audience words have to
  // follow a locale switch like everything else on the page (PRD-27 F-3, LOC-012).
  const t = useT().marketplace.gallery.about

  if (audience.kind !== 'groups' || audience.groups.length === 0) {
    return (
      <span className={className}>
        {audience.kind === 'everyone'
          ? t.audienceEveryone
          : audience.kind === 'private'
            ? t.audiencePrivate(owner)
            : t.audienceNobody}
      </span>
    )
  }

  const last = audience.groups.length - 1
  return (
    <span className={clsx('inline-flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1', className)}>
      {audience.groups.map((group, index) => (
        <Fragment key={group.id}>
          <span className="inline-flex min-w-0 items-center">
            <GroupLabel group={group} size={16} to />
            {index < last - 1 && <span>,</span>}
          </span>
          {index === last - 1 && <span>{t.audienceAnd}</span>}
        </Fragment>
      ))}
    </span>
  )
}
